import { EmployeeEmploymentRelationshipDTO } from '@Models/model.types';
import useSWRInfinite from 'swr/infinite';
import { addFiltersToApiParam, FilterState } from '@Component/Filter/utils';
import { APIError, ListResponse, UseFetchPaginatedState, useFetchPaginatedState } from '@tlx/astro-shared';
import { computeUrl } from '@Util/api';
import { APIUrls } from '@Util/urls.ts';

interface Props {
    year: number;
    month: number;
    filters: FilterState;
    fetchAll?: boolean;
    sort?: { column: string; direction: string };
}

// The return value here has function loadMore and the server provides data that makes it easy to calculate hasMore (happens in useFetchPaginatedState). [Kristoffer Kvello, 2023-08-22]
export function useEmployees(params: Props): UseFetchPaginatedState<EmployeeEmploymentRelationshipDTO, APIError> {
    const pageSize = 50;

    const response = useSWRInfinite<ListResponse<EmployeeEmploymentRelationshipDTO>, APIError>(
        (pageIndex: number, previousPageData: ListResponse<EmployeeEmploymentRelationshipDTO>) => {
            if (previousPageData && previousPageData.fullResultSize === 0) {
                return null;
            } else {
                return getUrl({
                    params,
                    from: pageIndex * pageSize,
                    count: params.fetchAll ? 0 : pageSize,
                });
            }
        },
        { shouldRetryOnError: false },
    );
    // response is { data, size, setSize }. The "infinite" part is that we call setSize +1 whenever hasMore is true and we're ready to receive more.
    // Then we call loadMore from the UseFetchPaginatedState value. [Kristoffer Kvello, 2023-08-22]
    return useFetchPaginatedState(response);
}

interface UrlProps {
    params: Props;
    from: number;
    count: number;
}
function getUrl({ params, from, count }: UrlProps): string {
    const apiParams = new URLSearchParams();
    if (params.sort?.column !== 'STATUS' && params.sort?.column !== 'PREVIOUS_SALARIES') {
        apiParams.append('count', count.toString());
    }
    apiParams.append('from', from.toString());
    apiParams.append('year', params.year.toString());
    apiParams.append('month', params.month.toString());
    if (params.sort?.direction) {
        const apiSort = mapTableSortDirectionToApiSort(params.sort.direction);
        if (apiSort) {
            apiParams.append('orderDirection', apiSort);
        }
    }

    if (params.sort?.column) {
        apiParams.append('orderBy', params.sort.column);
    }

    addFiltersToApiParam(apiParams, params.filters);
    apiParams.append(
        'fields',
        EMPLOYEE_FIELDS + ',' + EMPLOYMENT_FIELDS + ',' + VALIDATION_RESULT_FIELDS + ',' + PREVIOUS_SALARIES_FIELDS,
    );

    return computeUrl(APIUrls.VALID_EMPLOYEES, apiParams);
}

const EMPLOYEE_FIELDS =
    'employee(id,number,isContact,pictureId,displayName,department(id,displayName),employeeCategory(id,displayName))';
const EMPLOYMENT_FIELDS = 'employments(id,startDate,endDate,division(id,name,organizationNumber))';
const VALIDATION_RESULT_FIELDS =
    'validationResults(warnings(message,href,linkDescription),validations(message,href, linkDescription))';
const PREVIOUS_SALARIES_FIELDS =
    'previousSalaries(salaryTransactionId, wagePaymentIdOfEmployee, displayName, isAccessibleToLoggedInUser)';

const mapTableSortDirectionToApiSort = (direction: string): string | undefined => {
    switch (direction) {
        case 'ascending':
            return 'ASC';
        case 'descending':
            return 'DESC';
        default:
            return undefined;
    }
};
